import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@src/store/rootState";
import { getCopyKey } from "@src/store/slices/experienceBuilder/common/utils/getCopyKey";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import {
  EmbeddedMod,
  GroupedOtherMods,
} from "@src/store/slices/experienceBuilder/types";
import { selectedVariations } from "@src/store/slices/variations";

export const getOtherGroupsMods = createSelector(
  (state: RootState) => state.experienceBuilder.mods,
  experienceBuilderSlice.selectors.getVariationId,
  selectedVariations,
  (mods, variationId, variations): GroupedOtherMods[] => {
    const currentMods = mods.filter((m) => m.variationId === variationId);
    const currentModMap = new Map(currentMods.map((m) => [getCopyKey(m), m]));
    const otherMods = mods.filter((m) => m.variationId !== variationId);
    const variationMap = new Map(variations.map((v) => [v.id, v.name]));

    const groups = new Map<string, GroupedOtherMods>();
    const addMod = (key: string, mod: EmbeddedMod) => {
      let group = groups.get(key);
      if (group === undefined) {
        group = { selector: key, mods: [] };
        groups.set(key, group);
      }
      group.mods.push({
        variationId: mod.variationId,
        variationName: variationMap.get(mod.variationId) || "Unknown",
        mod,
      });
    };

    for (const mod of otherMods) {
      const key = getCopyKey(mod);
      if (!currentModMap.has(key)) {
        addMod(key, mod);
      }
    }

    return Array.from(groups.values());
  }
);
